import React, { FC, ForwardedRef, forwardRef } from 'react';
import { CoreButtonBase, CoreButtonBaseProps } from './core-button-base.component';
import { FadeSpinner } from '../fade-spinner';
import { useTheme } from "@mui/material/styles";
import { Colors } from "../../../constants";

type CoreButtonVariant = 'primary' | 'secondary' | 'danger';

const getTextColor = (variant: CoreButtonVariant) => {
	switch (variant) {
		case "danger":
			return Colors.red;
		case "primary":
			return Colors.orange;
		case "secondary":
			return Colors.black;
	}
};

const getFocusColor = (variant: CoreButtonVariant) => {
	switch (variant) {
		case "danger":
			return Colors.redPressed;
		case "primary":
			return Colors.orangePressed;
		case "secondary":
			return Colors.blackPressed;
	}
};

export type CoreButtonProps = Omit<CoreButtonBaseProps, 'variant'> & {
	loading?: boolean;
	loaderSize?: number;
	variant?: CoreButtonVariant;
};

export const CoreButton = forwardRef((props: Omit<CoreButtonProps, 'ref'>, ref: ForwardedRef<HTMLButtonElement>) => {
	const {
		loading,
		loaderSize = 17,
		disabled,
		children,
		sx,
		variant = 'primary',
		...buttonProps
	} = props;

	const theme = useTheme();

	return (
		<CoreButtonBase
			{...buttonProps}
			ref={ref}
			variant={variant}
			sx={[
				{
					width: 'auto',
					borderRadius: '50px',
					border: 'unset',
					backgroundColor: getTextColor(variant),
					color: Colors.white,

					'&:hover, &.Mui-focusVisible, &.core-button-loading': {
						backgroundColor: getFocusColor(variant),
					},

					'&:active': {
						color: Colors.white,
						backgroundColor: getFocusColor(variant),
					},
				},
				...Array.isArray(sx) ? sx : [sx],
			]}
			disabled={loading || disabled}
			className={loading ? "core-button-loading" : undefined}
		>
			{loading ? (
				<FadeSpinner
					size={loaderSize}
					height={5}
					width={2}
					borderRadius={1}
					color={Colors.white}
				/>
			) : children}
		</CoreButtonBase>
	);
});
